import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { map, values, keys, pickBy, size, pull, isEmpty, reduce } from 'lodash'
import cn from 'classnames'

import VoidItemsHeader from './VoidItemsHeader'
import OrderInformation from './OrderInformation'
import TabOrderItems from '../TabOrderItems'
import SidebarModal from '../../SidebarModal'
import Button from '../../Button'
import BackChevron from '../../BackChevron'

import useManagerApproval from '../../../hooks/useManagerApproval'
import confirmationNotification from '../../../hooks/useConfirmationNotification'

import styles from './Void.module.scss'

// Store comped boolean separately so that reasons and consumption are not intertwined.
const refundReasons = {
  '1': { id: '1', reason: 'Incorrectly entered order', isComp: false },
  '2': { id: '2', reason: 'Insufficient inventory', isComp: false },
  '3': { id: '3', reason: 'Item returned, quality related', isComp: false },
  '4': { id: '4', reason: 'Guest changed their mind', isComp: false },
  '5': { id: '5', reason: 'Long wait for item', isComp: false },
  '6': { id: '6', reason: 'Comped items', isComp: true },
}

const Void = ({ orderId, orderNumber, modifiedAt, items, itemsWithModifiers, subtotal, total, suiteInformation, userNotes, payment, remoteOrderTotal, onRequestVoid, onClose, frozen, className }) => {
  const classNames = cn(styles.void, className)
  const history = useHistory()

  // For all selected:
  // const [selectedItemIndexes, setSelectedItemIndexes] = useState(itemsWithModifiers.map((_, index) => index))
  const [selectedItemIndexes, setSelectedItemIndexes] = useState([])
  const [lockedItemIndexes, setLockedItemIndexes] = useState(map(keys(pickBy(itemsWithModifiers, 'refund')), Number))
  const [selectedReasonId, setSelectedReason] = useState('')
  // For all selected:
  // const [isEntireOrder, setIsEntireOrder] = useState(size(lockedItemIndexes) !== size(itemsWithModifiers))
  const [isEntireOrder, setIsEntireOrder] = useState(false)
  const [approvalModal, requestApproval] = useManagerApproval()

  const toggleEntireOrder = () => {
    if (size(lockedItemIndexes) === size(itemsWithModifiers)) return
    const nextState = !isEntireOrder
    setSelectedItemIndexes(nextState === true ? itemsWithModifiers.map((_, index) => index) : [])
    setLockedItemIndexes(nextState === true ? reduce(itemsWithModifiers, (lockedIndexes, item, index) => item.modifiers ? lockedIndexes : [ ...lockedIndexes, index ], []) : [])
    setIsEntireOrder(nextState)
  }

  const onSelectItem = (index) => {
    const isChecked = selectedItemIndexes.includes(index)

    let x = []
    for (let i = 0; i < itemsWithModifiers?.[index]?.modifiers?.length; i++) {
      x.push(index + i + 1)
    }

    let newSelected = []
    let newLocked = []
    if (isChecked) {
      newSelected = [...pull(selectedItemIndexes, index, ...x)]
      newLocked = [...pull(lockedItemIndexes, ...x)]
    } else {
      newSelected = [index, ...x, ...selectedItemIndexes]
      newLocked = [...x, ...lockedItemIndexes]
    }

    setSelectedItemIndexes(newSelected)
    setLockedItemIndexes(newLocked)
    const entireOrder = newSelected.length === itemsWithModifiers.length
    setIsEntireOrder(entireOrder)
  }

  const voidItems = () => {
    const itemIds = map(selectedItemIndexes, (itemIndex) => itemsWithModifiers?.[itemIndex]?.id)
    const lineItemIds = map(selectedItemIndexes, (itemIndex) => itemsWithModifiers?.[itemIndex]?.lineItemUuid)
    const reason = refundReasons?.[selectedReasonId]?.reason ?? 'Unknown'
    const isComp = refundReasons?.[selectedReasonId]?.isComp ?? false

    onRequestVoid({ orderId, itemIndexes: selectedItemIndexes, itemIds, lineItemIds, reason, isComp })
    setSelectedItemIndexes([])
    setSelectedReason('')
    setIsEntireOrder(false)
  }

  const handleRequestVoid = () => {
    confirmationNotification(
      () =>
        requestApproval(() => {
          voidItems()
          onClose()
        }),
      frozen
    )
  }

  return (
    <SidebarModal
      show
      title="Void Items"
      onClose={onClose}
      leftAccessory={<BackChevron onClick={() => history?.replace?.(`/tabs/${orderId}`)} />}
      className={classNames}
    >
      <div className={styles.bodyContainer}>
        <OrderInformation account={suiteInformation?.displayName} payment={payment} amount={remoteOrderTotal?.subtotalDisplay ?? subtotal} modifiedAt={modifiedAt} orderNumber={orderNumber} note={userNotes} />
        <VoidItemsHeader reasons={values(refundReasons)} selectedReasonId={selectedReasonId} entireOrder={isEntireOrder} onReasonSelected={setSelectedReason} onEntireOrderToggled={toggleEntireOrder} total={total} />
        <TabOrderItems selectable onSelectItem={onSelectItem} selectedItemIndexes={selectedItemIndexes} lockedItemIndexes={lockedItemIndexes} className={styles.selectableItemsContainer} items={itemsWithModifiers} />
        <div className={styles.buttonContainer}>
          <Button key='voidRequest' onClick={handleRequestVoid} className={styles.actionButton} disabled={isEmpty(selectedItemIndexes) || isEmpty(selectedReasonId)}>Request Void</Button>
        </div>
      </div>
      {approvalModal}
    </SidebarModal>
  )
}

export default Void
